import { CellPosition } from "@app/grid/GridCell";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { linkSourceKey } from "@app/grid/linkChain";
import Field, { AggregationType, FieldType, getDestinationField, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_OBJECT_KEY, TYPE_ITEMS_RELATION_FILTER, TYPE_ITEMS_RELATION_KEY } from "@dto/fields";
import { Period } from "@dto/period";
import { Direction, FieldColumn, Filter, INBOUND, Item, Relation } from "@dto/records";
import { TFunction } from "i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore } from "src/store";
import { addLinks, deleteLinks, getAllRowIdsFromData, getLinks, getRecordKeys, getRecords } from "./data";
import { getRelationDirection } from "./model";
import { toastError } from "./toasts";
import { guessNumberFormat, idToKey, isArray, removeNumberFormat, removeSpaces, removeSymbols, textToValue, valueToString } from "./types";


export const COPY_ITEM_MIME_TYPE = "text/plain";


// export function copy(item: Item, field: Field, aggregation: AggregationType, format: Map<string, any> | undefined | null, t: TFunction, fieldByKeys: Map<string, Field>) {
//     if (item.value === null || item.value === undefined) {
//         if (field.type === FieldType.Boolean) {
//             item.value = false;
//         } else {
//             return;
//         }
//     }
//     let copiedValue = valueToString(item.value, field, aggregation, format, t, "", fieldByKeys);
//     if (field.type === FieldType.Link) {
//         const destFieldKey = field.typeItems?.get(TYPE_ITEMS_FIELD_KEY);
//         if (destFieldKey) {
//             const destField = fieldByKeys?.get(destFieldKey);
//             if (destField) {
//                 if (isArray(item.value)) {
//                     copiedValue = item.value.map((linkedItem: Item) =>
//                         valueToString(linkedItem.value, destField, aggregation, format, t, "", fieldByKeys) + "@" + linkedItem.key?.recordKey
//                     ).join(",");
//                 } else {
//                     copiedValue = valueToString(item.value, destField, aggregation, format, t, "", fieldByKeys) + "@" + item.key?.recordKey;
//                 }
//                 if (destField.type  === FieldType.Number || destField.type === FieldType.Decimal || destField.type === FieldType.Devise || destField.type === FieldType.Percent) {
//                     copiedValue = removeFormat(copiedValue, destField, format);
//                 }
//             }
//         }
//     } else if (field.type === FieldType.Number || field.type === FieldType.Decimal || field.type === FieldType.Devise || field.type === FieldType.Percent) {
//         copiedValue = removeFormat(copiedValue, field, format);
//     }
//     navigator.clipboard.write([new ClipboardItem({
//         [COPY_ITEM_MIME_TYPE]: new Blob([copiedValue], { type: COPY_ITEM_MIME_TYPE })
//     })]);
// }

export function getSelectedCells(): Map<string, Map<number, { item: Item; fieldColumn: FieldColumn }>> {
    const allFocusedCells = useDataStore.getState().focusPos;
    if (!allFocusedCells || allFocusedCells.length === 0) {
        return new Map<string, Map<number, { item: Item; fieldColumn: FieldColumn }>>();
    }

    const metaLines = useDataStore.getState().metaLines;
    const fieldColumns = useDataStore.getState().fieldColumns;

    // Organize cells by row (rowId) to create a table
    const cellsByRow = new Map<string, Map<number, { item: Item; fieldColumn: FieldColumn }>>();

    allFocusedCells.forEach((position: CellPosition) => {
        const metaLine = metaLines.get(position.rowId);
        if (metaLine && position.column < metaLine.line.items.length) {
            const item = metaLine.line.items[position.column];
            const fieldColumn = fieldColumns[position.column];

            if (item && fieldColumn) {
                if (!cellsByRow.has(position.rowId)) {
                    cellsByRow.set(position.rowId, new Map());
                }
                cellsByRow.get(position.rowId)!.set(position.column, { item, fieldColumn });
            }
        }
    });

    return cellsByRow;
}

async function getPastedItem({
    item,
    field,
    valueStr,
    t,
    aggregation,
    format,
    fieldByKeys,
    fieldColumn,
    rowRecordKey,
}: {
    item: Item;
    field: Field;
    valueStr: string;
    t: TFunction;
    aggregation: AggregationType;
    format: Map<string, any> | undefined | null;
    fieldByKeys: Map<string, Field> | null | undefined;
    fieldColumn: FieldColumn | null;
    // Record of the current object in the row of the cell
    rowRecordKey?: string;
}): Promise<PastedItem | null> {
    try {
        if (field.type === FieldType.Link) {
            const relationByKeys = useModelStore.getState().relationByKeys;
            const workspace = useModelStore.getState().selectedWorkspace;
            const objectKey = useModelStore.getState().selectedObjectInfos?.object._key;
            // Record whose link is set (for a link chain: the row record, not the parent of the item)
            const sourceKey = linkSourceKey(item, field, fieldByKeys, objectKey, rowRecordKey);
            const parentRecordKey = sourceKey?.recordKey;
            const originObjKey = sourceKey?.objectKey;
            if (workspace?._key && relationByKeys && parentRecordKey && originObjKey) {
                const relationKey = field.typeItems?.get(TYPE_ITEMS_RELATION_KEY);
                const fieldKey = field.typeItems?.get(TYPE_ITEMS_FIELD_KEY);
                const objKey = field.typeItems?.get(TYPE_ITEMS_OBJECT_KEY);
                const relationFilter = field.typeItems?.get(TYPE_ITEMS_RELATION_FILTER) as Filter | null;
                const parentObjectKey = originObjKey;
                const relation = relationByKeys?.get(relationKey);
                // Field whose value is pasted: the end of the chain for a link chain
                const destField = getDestinationField(field, fieldByKeys ?? null);
                const linkFormat = destField?.format;
                if (!destField) {
                    return null;
                }

                const value = textToValue(valueStr, destField, t, aggregation, linkFormat, fieldByKeys as Map<string, Field> | null);
                const possibleRecords = await getRecords(workspace._key, objKey, null, [fieldKey], relationFilter);
                let matchingRecordKey: string | null = null;
                for (const record of possibleRecords) {
                    let recordValue = record.fields.get(destField._key);
                    if (destField.type === FieldType.Period) {
                        recordValue = Period.fromObject(recordValue);
                        if (recordValue && recordValue.date === value.date && recordValue.type === value.type) {
                            matchingRecordKey = record._key;
                            break;
                        }
                    } else if (destField.type === FieldType.Choice || destField.type === FieldType.MultiChoice) {
                        const recordStrValue = valueToString(recordValue, destField, AggregationType.NO_AGGR, linkFormat, t, "", fieldByKeys as Map<string, Field> | null)
                        const valueStr = valueToString(value, destField, AggregationType.NO_AGGR, linkFormat, t, "", fieldByKeys as Map<string, Field> | null)
                        if (recordStrValue === valueStr) {
                            matchingRecordKey = record._key;
                            break;
                        }
                    } else if (destField.type === FieldType.Number || destField.type === FieldType.Decimal || destField.type === FieldType.Devise || destField.type === FieldType.Percent) {
                        let recordStrValue = valueToString(recordValue, destField, AggregationType.NO_AGGR, linkFormat, t, "", fieldByKeys as Map<string, Field> | null)
                        recordStrValue = removeSpaces(recordStrValue);
                        recordStrValue = removeSymbols(recordStrValue);
                        let valueStrValue = removeSpaces(valueStr);
                        valueStrValue = removeSymbols(valueStrValue);
                        if (recordStrValue === valueStrValue) {
                            matchingRecordKey = record._key;
                            break;
                        }
                    } else if (recordValue === value) {
                        matchingRecordKey = record._key;
                        break;
                    }
                }

                const direction: Direction = getRelationDirection(relation, parentObjectKey);
                let currentLinks: Relation[] = [];
                const linksResponse = await getLinks(workspace._key, originObjKey, relationKey, [parentRecordKey], direction);
                if (linksResponse) {
                    if (isCompozzError(linksResponse)) {
                        const cError = linksResponse as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    } else {
                        currentLinks = linksResponse as Relation[];
                    }
                }

                if (matchingRecordKey) {
                    const addLinksResponse = await addLinks(workspace._key, originObjKey, relationKey, parentRecordKey, [matchingRecordKey], direction);
                    if (addLinksResponse) {
                        if (isCompozzError(addLinksResponse)) {
                            const cError = addLinksResponse as CompozzError;
                            if (cError.i18n) {
                                toastError(translate(t, cError.i18n, cError.params));
                            } else {
                                toastError(cError.message);
                            }
                        } else {
                            const linksAdded = addLinksResponse as Relation[];
                            const linksToRemove = currentLinks.filter(link => {
                                if (linksAdded.some(linkAdded => linkAdded._key === link._key)) {
                                    return false;
                                }

                                if (direction === INBOUND) {
                                    return idToKey(link._from) !== matchingRecordKey;
                                }

                                return idToKey(link._to) !== matchingRecordKey;
                            });
                            if (linksToRemove.length > 0) {
                                const deleteLinksResponse = await deleteLinks(workspace._key, originObjKey, parentRecordKey, relationKey, direction, linksToRemove.map(link => link._key));
                                if (deleteLinksResponse) {
                                    if (isCompozzError(deleteLinksResponse)) {
                                        const cError = deleteLinksResponse as CompozzError;
                                        if (cError.i18n) {
                                            toastError(translate(t, cError.i18n, cError.params));
                                        } else {
                                            toastError(cError.message);
                                        }
                                    }
                                }
                            }
                        }
                    }
                } else {
                    const linksToRemove = currentLinks.filter(link => {
                        if (direction === INBOUND) {
                            return idToKey(link._from) !== matchingRecordKey;
                        }

                        return idToKey(link._to) !== matchingRecordKey;
                    });
                    if (linksToRemove.length > 0) {
                        const deleteLinksResponse = await deleteLinks(workspace._key, originObjKey, parentRecordKey, relationKey, direction, linksToRemove.map(link => link._key));
                        if (deleteLinksResponse) {
                            if (isCompozzError(deleteLinksResponse)) {
                                const cError = deleteLinksResponse as CompozzError;
                                if (cError.i18n) {
                                    toastError(translate(t, cError.i18n, cError.params));
                                } else {
                                    toastError(cError.message);
                                }
                            }
                        }
                    }
                }
            }

            return null;
        }

        if (field.type === FieldType.Decimal || field.type === FieldType.Number || field.type === FieldType.Devise || field.type === FieldType.Percent) {
            const numberFormat = guessNumberFormat(valueStr);
            const unformattedValue = removeNumberFormat(valueStr, numberFormat);
            return { item: { ...item, value: unformattedValue }, fieldColumn };
        }

        const value = textToValue(valueStr, field, t, aggregation, format, fieldByKeys as Map<string, Field> | null);
        return { item: { ...item, value }, fieldColumn };
    } catch (error) {
        console.error("Error parsing clipboard item", error);
        return null;
    }
}

export async function paste(item: Item, field: Field, aggregation: AggregationType, format: Map<string, any> | undefined | null, t: TFunction): Promise<PastedItem | null> {
    const items = await navigator.clipboard.read();
    const clipboardItem = items.find(item => item.types.includes(COPY_ITEM_MIME_TYPE));
    const fieldByKeys = useModelStore.getState().fieldByKeys;
    if (clipboardItem) {
        const blob = await clipboardItem.getType(COPY_ITEM_MIME_TYPE);
        const valueStr = await blob.text();
        return getPastedItem({ item, field, valueStr, t, aggregation, format, fieldByKeys, fieldColumn: null });
    }
    return null;
}

// parse the clipboard item into a table
// Parsing the clipboard item into a table is a bit tricky because it can contain quotes and tabs
// So we need to parse the clipboard characters one by one and build the table row by row
function parseClipboardItem(valueStr: string): string[][] {
    const text = valueStr.replaceAll("\r\n", "\n").replaceAll("\r", "\n");
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentCell = "";
    let inQuotes = false;

    const pushCell = () => {
        currentRow.push(currentCell.trim());
        currentCell = "";
    };

    const pushRow = () => {
        pushCell();
        rows.push(currentRow);
        currentRow = [];
    };

    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const nextChar = text[i + 1];

        if (char === "\"") {
            if (inQuotes && nextChar === "\"") {
                currentCell += "\"";
                i++;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }

        if (!inQuotes && char === "\t") {
            pushCell();
            continue;
        }

        if (!inQuotes && char === "\n") {
            pushRow();
            continue;
        }

        currentCell += char;
    }

    if (currentCell !== "" || currentRow.length > 0) {
        pushRow();
    }

    return rows;
}

interface PasteAllOptions {
    confirmBeforeMultiCellPaste?: (selectedCellCount: number) => boolean | Promise<boolean>;
}

interface PastedItem {
    item: Item;
    fieldColumn: FieldColumn | null;
}

export async function pasteAll(t: TFunction, options?: PasteAllOptions): Promise<PastedItem[]> {
    const items = await navigator.clipboard.read();
    const clipboardItem = items.find(item => item.types.includes(COPY_ITEM_MIME_TYPE));
    if (clipboardItem) {
        const blob = await clipboardItem.getType(COPY_ITEM_MIME_TYPE);
        const valueStr = await blob.text();

        // Parse the clipboard item into a table
        const lines = parseClipboardItem(valueStr);

        // Organize cells by row (rowId) to create a table
        const cellsByRow = getSelectedCells();

        if (lines.length === 0) {
            return [];
        }

        if (lines.some(line => line.length > 1)) {
            toastError(translate(t, "copy.clipboard.error.pasting.several.columns", []));
            return [];
        }


        let hasSeveralSelectedColumns = false;
        let columnIdx: number | null = null;
        cellsByRow.forEach((rowCells) => {
            rowCells.forEach((_cell, column) => {
                if (columnIdx === null) {
                    columnIdx = column;
                } else if (columnIdx !== column) {
                    hasSeveralSelectedColumns = true;
                    return;
                }
            });
        });
        if (columnIdx === null) {
            return [];
        }
        if (hasSeveralSelectedColumns) {
            toastError(translate(t, "copy.clipboard.error.pasting.on.several.columns", []));
            return [];
        }

        if (lines.length !== 1 && lines.length !== cellsByRow.size) {
            toastError(translate(t, "copy.clipboard.error.not.same.line.number", [lines.length, cellsByRow.size]));
            return [];
        }

        // Confirm before pasting into multiple cells
        const selectedCellCount = Array.from(cellsByRow.values()).reduce((count, rowCells) => count + rowCells.size, 0);
        if (selectedCellCount > 1 && options?.confirmBeforeMultiCellPaste) {
            const confirmed = await options.confirmBeforeMultiCellPaste(selectedCellCount);
            if (!confirmed) {
                return [];
            }
        }

        const allFocusedCells = useDataStore.getState().focusPos;
        if (!allFocusedCells || allFocusedCells.length === 0) {
            return [];
        }

        const selectedView = useModelStore.getState().selectedView;
        const fieldByKeys = useModelStore.getState().fieldByKeys;
        const objectKey = useModelStore.getState().selectedObjectInfos?.object._key;
        const metaLines = useDataStore.getState().metaLines;
        const rowRecordKey = (rowId: string): string | undefined => {
            const metaLine = metaLines.get(rowId);
            return metaLine && objectKey ? getRecordKeys(metaLine, objectKey, true)[0] : undefined;
        };


        let newItems: PastedItem[] = [];
        if (lines.length === 1) {
            const pastedItems = await Promise.all(
                Array.from(cellsByRow.entries()).flatMap(([rowId, rowCells]) =>
                    Array.from(rowCells.values()).map(async (cell) => {
                        const { item, fieldColumn } = cell;
                        const field = fieldColumn.srcField || fieldColumn.field;
                        const pathKey = fieldColumn.pathKey ?? "";
                        const aggregation = selectedView?.aggregations?.get(pathKey) ?? field?.aggregation ?? AggregationType.NO_AGGR;
                        const format = field?.format;

                        return getPastedItem({ item, field, valueStr, t, aggregation, format, fieldByKeys, fieldColumn, rowRecordKey: rowRecordKey(rowId) });
                    })
                )
            );

            newItems = pastedItems.filter((newItem): newItem is PastedItem => newItem !== null);
        } else {
            for (let i = 0; i < lines.length; i++) {
                const rowId = Array.from(cellsByRow.keys())[i];
                const rowCells = cellsByRow.get(rowId);
                if (!rowCells) {
                    continue;
                }
                const columnId = Array.from(rowCells.keys())[0];
                const cell = rowCells.get(columnId);
                if (!cell) {
                    continue;
                }
                const { item, fieldColumn } = cell;
                const field = fieldColumn.srcField || fieldColumn.field;
                const pathKey = fieldColumn.pathKey ?? "";
                const aggregation = selectedView?.aggregations?.get(pathKey) ?? field?.aggregation ?? AggregationType.NO_AGGR;
                const format = field?.format;
                const valueStr = lines[i][0];
                const newItem = await getPastedItem({ item, field, valueStr, t, aggregation, format, fieldByKeys, fieldColumn, rowRecordKey: rowRecordKey(rowId) });
                if (newItem) {
                    newItems.push(newItem);
                }
            }
        }
        return newItems;
    }
    return [];
}

export function copyAll(t: TFunction) {
    const allFocusedCells = useDataStore.getState().focusPos;
    if (!allFocusedCells || allFocusedCells.length === 0) {
        return;
    }

    const selectedView = useModelStore.getState().selectedView;
    const fieldByKeys = useModelStore.getState().fieldByKeys;

    // Organize cells by row (rowId) to create a table
    const cellsByRow = getSelectedCells();

    // Find min and max columns to create a rectangular table
    const allColumns = new Set<number>();
    cellsByRow.forEach((rowCells) => {
        rowCells.forEach((_, column) => {
            allColumns.add(column);
        });
    });

    if (allColumns.size === 0) {
        return;
    }

    const minColumn = Math.min(...Array.from(allColumns));
    const maxColumn = Math.max(...Array.from(allColumns));

    // Get rowIds in table order (preserving grouping/sorting)
    const data = useDataStore.getState().data;
    const allRowIdsInOrder = getAllRowIdsFromData(data);

    // Filter to only selected rowIds, maintaining table order
    const rowIds = allRowIdsInOrder.filter(rowId => cellsByRow.has(rowId));

    // Build the text to copy (TSV format - Tab Separated Values)
    const rows: string[] = [];

    rowIds.forEach((rowId) => {
        const rowCells = cellsByRow.get(rowId)!;
        const rowValues: string[] = [];

        for (let col = minColumn; col <= maxColumn; col++) {
            const cellData = rowCells.get(col);
            if (cellData) {
                const { item, fieldColumn } = cellData;
                const field = fieldColumn.srcField || fieldColumn.field;
                const pathKey = fieldColumn.pathKey ?? "";
                let format = selectedView?.formats?.get(pathKey) ?? field?.format;
                const aggregation = selectedView?.aggregations?.get(pathKey) ?? field?.aggregation ?? AggregationType.NO_AGGR;

                let valueStr = "";

                // Special handling for links
                if (field.type === FieldType.Link) {
                    const destFieldKey = field.typeItems?.get(TYPE_ITEMS_FIELD_KEY);
                    if (destFieldKey) {
                        const destField = fieldByKeys?.get(destFieldKey);
                        if (destField) {
                            format = destField?.format;
                            if (isArray(item.value) && destField.type !== FieldType.Choice && destField.type !== FieldType.MultiChoice) {
                                valueStr = item.value.map((linkedItem: Item) =>
                                    valueToString(linkedItem.value, destField, aggregation, format, t, "", fieldByKeys)
                                ).join(",");
                            } else {
                                valueStr = valueToString(item.value, destField, aggregation, format, t, "", fieldByKeys);
                            }
                            if (destField.type === FieldType.Number || destField.type === FieldType.Decimal || destField.type === FieldType.Devise || destField.type === FieldType.Percent) {
                                valueStr = removeSpaces(valueStr);
                                valueStr = removeSymbols(valueStr);
                            } else if (destField.type === FieldType.TextArea) {
                                valueStr = valueToString(item.value, destField, aggregation, format, t, "", fieldByKeys);
                                valueStr = "\"" + valueStr + "\"";
                            }
                        }
                    }
                } else if (field.type === FieldType.Number || field.type === FieldType.Decimal || field.type === FieldType.Devise || field.type === FieldType.Percent) {
                    valueStr = valueToString(item.value, field, aggregation, format, t, "", fieldByKeys);
                    valueStr = removeSpaces(valueStr);
                    valueStr = removeSymbols(valueStr);
                } else if (field.type === FieldType.TextArea) {
                    valueStr = valueToString(item.value, field, aggregation, format, t, "", fieldByKeys);
                    valueStr = "\"" + valueStr + "\"";
                } else {
                    valueStr = valueToString(item.value, field, aggregation, format, t, "", fieldByKeys);
                }

                rowValues.push(valueStr);
            } else {
                // Empty cell in rectangular selection
                rowValues.push("");
            }
        }

        rows.push(rowValues.join("\t"));
    });

    const copiedText = rows.join("\n");

    // Copy to clipboard
    navigator.clipboard.write([new ClipboardItem({
        [COPY_ITEM_MIME_TYPE]: new Blob([copiedText], { type: COPY_ITEM_MIME_TYPE })
    })]);
}